import { useMemo } from 'react';
import {
  useQuery,
  useQueryClient,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';

import {
  DeviceTrendPoints,
  QueryTrendsRequestBody,
  QueryTrendsResult,
} from '@repo/sdk/models/components';
import { SDKError } from '@repo/sdk/models/errors';

import { useCurrentFacility } from '@/hooks/use-current-facility';
import { useGaiaClient } from '@/hooks/use-gaia-client';
import { listDeviceTrendPointsQuery } from '@/queries/gaia-api/list-device-trend-points-query';
import { queryTrendsQuery } from '@/queries/gaia-api/query-trends-query';

export interface UseQueryTrendsProps<TData = QueryTrendsResult> {
  body: QueryTrendsRequestBody;
  /** Semantic duration for preset live queries; keeps cache keys stable while request start rolls. */
  liveWindowMs?: number;
  /** Maps successful `QueryTrendsResult` (e.g. to chart render shape). */
  select?: (data: QueryTrendsResult) => TData;
}

/**
 * Hook for fetching trend data via the query_trends POST endpoint.
 * Uses hybrid cache key: live and historical queries dedupe by request body hash
 * (identical bodies share one cache entry).
 * Pass a stable `body` (e.g. from useMemo with primitive deps) when possible
 * so the query options are not recomputed every render.
 */
export const useQueryTrends = <TData = QueryTrendsResult>({
  body,
  liveWindowMs,
  select,
}: UseQueryTrendsProps<TData>): UseQueryResult<TData, SDKError> => {
  const client = useGaiaClient();
  const queryClient = useQueryClient();
  const { agentId } = useCurrentFacility();

  const options = useMemo(
    () => queryTrendsQuery<TData>({ agentId, body, client, queryClient, liveWindowMs, select }),
    [agentId, body, client, queryClient, liveWindowMs, select]
  );
  return useQuery(options);
};

/**
 * Hook for fetching device trend points from Gaia BFF.
 * This hook retrieves available data points for trending for a given agent.
 *
 * @returns Query result containing device trend points with their associated display refs
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useListDeviceTrendPoints();
 *
 *  data contains DeviceTrendPoints[] with structure:
 *  {
 *    deviceId: string,
 *    alias: string,
 *    displayName: string,
 *    allDisplayRefs: DisplayRef[],      // All available trend points
 *    templateDisplayRefs: DisplayRef[]  // Template-specific trend points
 *  }
 * ```
 */
export const useListDeviceTrendPoints = (): UseSuspenseQueryResult<
  DeviceTrendPoints[] | undefined
> => {
  const client = useGaiaClient();
  const { agentId } = useCurrentFacility();

  return useSuspenseQuery(
    listDeviceTrendPointsQuery({
      agentId: agentId,
      client,
    })
  );
};
